import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Avatar, Chip,
  TextField, MenuItem, Button, IconButton, Tooltip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, Dialog, DialogContent, DialogActions, InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Savings as PettyIcon, AccountBalance as BankIcon,
  Payments as PaymentIcon, AddCard as RequestIcon,
  TrendingUp as InflowIcon, TrendingDown as OutflowIcon,
  PriceCheck as CashIcon, Add as AddIcon, Close as CloseIcon,
  Save as SaveIcon, Search as SearchIcon, CalendarMonth as MonthIcon,
  Description as VoucherIcon, Delete as DeleteIcon,
  AccountTree as DoubleEntryIcon,
  ArrowForward as BFIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import { useIsMobile, MobileDataCard } from '../../components/ResponsiveTable';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const FONT_MONO = `'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};
const MON = ['January','February','March','April','May','June',
             'July','August','September','October','November','December'];
const monthYerOf = (date) => {
  const d = date ? new Date(date) : new Date();
  return `${MON[d.getMonth()]}/${d.getFullYear()}`;
};

const CASH = 'Cash-In-Hand';

// ── KPI tile ─────────────────────────────────────────────────
function KpiTile({ icon, label, value, sub, tone = 'primary' }) {
  return (
    <Card sx={{
      flex: 1, minWidth: 200, borderRadius: 2,
      borderLeft: (t) => `4px solid ${t.palette[tone].main}`,
      transition: 'transform .15s, box-shadow .15s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
    }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.6, py: 1.6 }}>
        <Box sx={{
          width: 46, height: 46, borderRadius: '50%',
          bgcolor: (t) => alpha(t.palette[tone].main, 0.14),
          color: `${tone}.main`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, ...TNUM, fontSize: 20, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {sub && <Typography variant="caption" color="text.secondary" noWrap>{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Reimbursement request dialog ─────────────────────────────
function ReimbursementDialog({ open, onClose, accounts, onSaved }) {
  const [form, setForm] = useState({
    date: todayISO(), source_account: CASH, amount: '', reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (open) {
      setForm({ date: todayISO(), source_account: CASH, amount: '', reason: '' });
      setError('');
    }
  }, [open]);

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await API.post('/petty/reimbursements', {
        date: form.date,
        source_account: form.source_account,
        amount: Number(form.amount),
        reason: form.reason,
      });
      // New pending reimbursement — bump the sidebar badge immediately.
      window.dispatchEvent(new Event('pendingCountsChanged'));
      onSaved && (await onSaved());
      onClose();
    } catch (e) { setError(e?.response?.data?.message || e.message || 'Submit failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 60%, ${t.palette.info.main} 100%)`,
        color: '#fff', px: 3, py: 2.2,
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
            <RequestIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
              Petty Cash · Reimbursement
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              Request Reimbursement
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Stack>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
          <Typography variant="caption" fontWeight={800}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5, color: 'text.secondary', display: 'block', mb: 1 }}>
            Reimbursement details
          </Typography>
          <Stack spacing={2}>
            <TextField select size="small" label="Source Account" required
              value={form.source_account}
              onChange={(e) => setForm({ ...form, source_account: e.target.value })}
              helperText="Where the money is being pulled from">
              <MenuItem value={CASH}>{CASH} (cash)</MenuItem>
              {accounts.filter(a => a.name !== CASH).map(a => (
                <MenuItem key={a.name} value={a.name}>{a.name} · {a.account_type || 'Bank'}</MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth size="small" type="date" label="Request Date" required
                InputLabelProps={{ shrink: true }}
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} />
              <TextField fullWidth size="small" type="number" label="Amount (GHS)" required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                inputProps={{ style: { textAlign: 'right', ...TNUM, fontWeight: 800, fontSize: 16 } }} />
            </Stack>
            <TextField multiline minRows={2} size="small" label="Reason" required
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              helperText="At least 5 characters" />
          </Stack>
        </Card>

        <Alert severity="info" variant="outlined" sx={{ mt: 2, borderRadius: 1.5 }}>
          <Typography variant="caption" fontWeight={700}>How this works</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.4, fontSize: 12 }}>
            <li>The request goes to <b>PENDING</b>. No money moves yet.</li>
            <li>An approver with the <i>Approve Petty Cash Reimbursements</i> right reviews and approves.</li>
            <li>On approval the source account is debited and Petty Cash gets the funds; both legs post atomically.</li>
          </Box>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.7, bgcolor: 'background.paper' }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" color="primary"
          onClick={submit}
          disabled={saving || !(Number(form.amount) > 0) || !form.reason || form.reason.trim().length < 5}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ fontWeight: 800, minWidth: 180, textTransform: 'none', boxShadow: 2 }}>
          {saving ? 'Submitting…' : 'Submit request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Petty voucher dialog ─────────────────────────────────────
function PettyVoucherDialog({ open, onClose, categories, expTypes, balance, onSaved }) {
  const [form, setForm] = useState({ date: todayISO(), amount: '', narration: '' });
  const [items, setItems] = useState([blankItem()]);
  const [voucherNo, setVN] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function blankItem() {
    return { exp_cat: '', exp_ty: '', amount: '', narration: '', request_holder: '' };
  }

  useEffect(() => {
    if (open) {
      setForm({ date: todayISO(), amount: '', narration: '' });
      setItems([blankItem()]);
      setError('');
      API.get('/petty/next-voucher-no', { params: { date: todayISO() } })
        .then(r => setVN(r.data?.voucher_no || '')).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (!open || !form.date) return;
    API.get('/petty/next-voucher-no', { params: { date: form.date } })
      .then(r => setVN(r.data?.voucher_no || '')).catch(() => {});
  }, [form.date, open]);

  const updateItem = (i, patch) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addItem = () => setItems(prev => [...prev, blankItem()]);
  const removeItem = (i) => setItems(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  // Auto-sync single-item amount with cheque total
  useEffect(() => {
    if (items.length === 1 && items[0].amount !== String(form.amount || '')) {
      setItems([{ ...items[0], amount: String(form.amount || '') }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.amount, items.length]);

  const itemsTotal = useMemo(
    () => items.reduce((s, it) => s + (Number(it.amount) || 0), 0),
    [items]
  );
  const total = Number(form.amount) || 0;
  const balanced = items.length > 0 && Math.abs(itemsTotal - total) < 0.01 && total > 0;
  const insufficient = total > 0 && total > Number(balance || 0) + 0.001;

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await API.post('/petty/vouchers', {
        date: form.date,
        amount: Number(form.amount),
        narration: form.narration,
        items: items.map(it => ({
          exp_cat: it.exp_cat,
          exp_ty: it.exp_ty,
          amount: Number(it.amount),
          narration: it.narration,
          request_holder: it.request_holder,
        })),
      });
      onSaved && (await onSaved());
      onClose();
    } catch (e) { setError(e?.response?.data?.message || e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 60%, ${t.palette.info.main} 100%)`,
        color: '#fff', px: 3, py: 2.2,
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
            <PaymentIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
              Petty Cash · Voucher
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              New Petty Cash Voucher
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 1.8 }}>
          <Box sx={{ flex: 1, p: 1.2, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)' }}>
            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 700, fontSize: 10 }}>VOUCHER NO.</Typography>
            <Typography fontWeight={800} sx={{ fontFamily: FONT_MONO, fontSize: 13.5 }}>{voucherNo || '—'}</Typography>
          </Box>
          <Box sx={{ flex: 1, p: 1.2, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)' }}>
            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 700, fontSize: 10 }}>PETTY CASH BALANCE</Typography>
            <Typography fontWeight={800} sx={{ fontSize: 14, ...TNUM }}>GHS {fmtMoney(balance)}</Typography>
          </Box>
          <Box sx={{ flex: 1, p: 1.2, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)' }}>
            <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 700, fontSize: 10 }}>ITEMS</Typography>
            <Typography fontWeight={800} sx={{ fontSize: 14 }}>{items.length} {items.length === 1 ? 'line' : 'lines'}</Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {insufficient && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Petty Cash only has GHS {fmtMoney(balance)} — not enough for this voucher. Request a reimbursement first.
          </Alert>
        )}

        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'background.paper' }}>
          <Typography variant="caption" fontWeight={800}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5, color: 'text.secondary', display: 'block', mb: 1 }}>
            Voucher Header
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <TextField size="small" type="date" label="Date" required
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <TextField size="small" type="number" label="Total Amount (GHS)" required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              inputProps={{ style: { textAlign: 'right', ...TNUM, fontWeight: 800, fontSize: 16 } }} />
            <TextField size="small" label="Voucher narration (optional)"
              value={form.narration}
              onChange={(e) => setForm({ ...form, narration: e.target.value })} />
          </Box>
        </Card>

        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'background.paper' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight={800}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5, color: 'text.secondary' }}>
              Items
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Chip size="small" variant="outlined"
              color={balanced ? 'success' : 'warning'}
              label={`Σ items: GHS ${fmtMoney(itemsTotal)} / ${fmtMoney(total)}`}
              sx={{ fontWeight: 800, fontSize: 11 }} />
          </Stack>

          <Box sx={{
            maxHeight: 320, overflowY: 'auto', pr: 0.5, mb: 1,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { bgcolor: (t) => alpha(t.palette.text.disabled, 0.3), borderRadius: 3 },
          }}>
            {items.map((it, i) => {
              const types = expTypes.filter(t => !it.exp_cat || t.cat === it.exp_cat);
              return (
                <Box key={i} sx={{
                  display: 'grid', gap: 1, mb: 1,
                  px: 1.2, py: 1.1, borderRadius: 1.5, bgcolor: 'grey.50',
                  border: '1px solid', borderColor: 'divider',
                  gridTemplateColumns: { xs: '1fr', md: '34px 1.4fr 1.4fr 1.1fr 110px 2fr 36px' },
                  alignItems: 'center',
                }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: 11, fontWeight: 800, bgcolor: 'primary.main' }}>{i + 1}</Avatar>
                  <TextField select size="small" label="Category" required
                    value={it.exp_cat}
                    onChange={(e) => updateItem(i, { exp_cat: e.target.value, exp_ty: '' })}>
                    <MenuItem value=""><em>— pick —</em></MenuItem>
                    {categories.map(c => <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Type" required
                    value={it.exp_ty}
                    onChange={(e) => updateItem(i, { exp_ty: e.target.value })}
                    disabled={!it.exp_cat}>
                    <MenuItem value=""><em>— pick —</em></MenuItem>
                    {types.map(t => <MenuItem key={t.name} value={t.name}>{t.name}</MenuItem>)}
                  </TextField>
                  <TextField size="small" label="Request Holder" required
                    value={it.request_holder}
                    onChange={(e) => updateItem(i, { request_holder: e.target.value })} />
                  <TextField size="small" type="number" label="Amount" required
                    value={it.amount}
                    onChange={(e) => updateItem(i, { amount: e.target.value })}
                    inputProps={{ style: { textAlign: 'right', ...TNUM, fontWeight: 800 } }}
                    disabled={items.length === 1} />
                  <TextField size="small" label="Narration" required
                    value={it.narration}
                    onChange={(e) => updateItem(i, { narration: e.target.value })} />
                  <Tooltip title={items.length === 1 ? 'Add another item below' : 'Remove this item'}>
                    <span>
                      <IconButton size="small" onClick={() => removeItem(i)} disabled={items.length === 1}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>

          <Stack direction="row" alignItems="center">
            <Button variant="outlined" size="small" startIcon={<AddIcon />}
              onClick={addItem}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Add another item
            </Button>
          </Stack>
        </Card>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.7, bgcolor: 'background.paper' }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" color="primary"
          onClick={submit}
          disabled={
            saving || !form.date || !(Number(form.amount) > 0)
            || !balanced || insufficient
            || items.some(it => !it.exp_cat || !it.exp_ty || !(Number(it.amount) > 0) || !it.narration?.trim() || !it.request_holder?.trim())
          }
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ fontWeight: 800, minWidth: 200, textTransform: 'none', boxShadow: 2 }}>
          {saving ? 'Posting…' : 'Post voucher'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function PettyPage() {
  const isMobile = useIsMobile();
  const [month, setMonth] = useState(monthYerOf(todayISO()));
  const [months, setMonths] = useState([]);
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expTypes, setExpTypes] = useState([]);

  const [reimOpen, setReimOpen] = useState(false);
  const [pvOpen,   setPvOpen]   = useState(false);

  const [reimQ, setReimQ] = useState('');
  const [voucherQ, setVoucherQ] = useState('');
  const [voucherPage, setVoucherPage] = useState(0);
  const [voucherRpp,  setVoucherRpp]  = useState(10);
  const [reimPage, setReimPage] = useState(0);
  const [reimRpp,  setReimRpp]  = useState(10);

  // Running journal: search + date range + pagination + DR/CR dialog
  const [journalQ,    setJournalQ]    = useState('');
  const [journalFrom, setJournalFrom] = useState('');
  const [journalTo,   setJournalTo]   = useState('');
  const [journalPage, setJournalPage] = useState(0);
  const [journalRpp,  setJournalRpp]  = useState(10);
  const [deOpen,      setDeOpen]      = useState(false);
  const [deUuid,      setDeUuid]      = useState('');
  const [deLabel,     setDeLabel]     = useState('');
  const openDoubleEntry = (uuid, label) => {
    setDeUuid(uuid); setDeLabel(label || ''); setDeOpen(true);
  };

  const loadRefs = async () => {
    try {
      const [bAcc, cats, types] = await Promise.all([
        API.get('/payments/bank-accounts'),
        API.get('/expenditures/categories'),
        API.get('/expenditures', { params: { perPage: 'all' } }),
      ]);
      setAccounts(bAcc.data || []);
      setCategories(cats.data || []);
      setExpTypes(types.data?.rows || []);
    } catch { /* silent */ }
  };
  const loadMonths = async () => {
    // Pull months from BOTH petty cash and cashbook so the dropdown
    // lists every working month in the system, not just months that
    // have petty activity. Useful when a fresh month has no petty
    // movement yet but the user wants to navigate to it.
    try {
      const [pet, cb] = await Promise.all([
        API.get('/petty/months').catch(() => ({ data: [] })),
        API.get('/cashbook/months').catch(() => ({ data: [] })),
      ]);
      const seen = new Map();
      for (const m of [...(pet.data || []), ...(cb.data || [])]) {
        if (!seen.has(m.mon_yer)) seen.set(m.mon_yer, m);
      }
      // Always include the current working month (in case both sources are empty)
      if (!seen.has(month)) seen.set(month, { mon_yer: month, first: todayISO() });
      const merged = Array.from(seen.values()).sort((a, b) => {
        const da = new Date(a.first || todayISO()).getTime();
        const db = new Date(b.first || todayISO()).getTime();
        return db - da; // newest first
      });
      setMonths(merged);
    } catch { setMonths([]); }
  };
  const loadSnap = async () => {
    setLoading(true);
    try {
      const r = await API.get('/petty/snapshot', { params: { month } });
      setSnap(r.data);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load petty cash')); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRefs(); loadMonths(); }, []);
  useEffect(() => { loadSnap(); /* eslint-disable-next-line */ }, [month]);

  const balance = Number(snap?.balance || 0);
  const summary = snap?.summary || { reimbursed: 0, expensed: 0 };
  const journal = snap?.journal || [];
  const vouchers = snap?.vouchers || [];
  const pendings = snap?.pending_reimbursements || [];
  // Snapshot now returns the unified reimbursements list (workflow +
  // legacy peety side='cr' rows) — no need for a separate API call.
  const reimList = snap?.reimbursements || [];
  const opening  = snap?.opening || { amount: 0, source: 'none', bf_from: null };

  const bringForward = async () => {
    const ok = await swal.confirm({
      title: `Bring forward Petty Cash B/F for ${month}?`,
      text: opening.source === 'computed'
        ? `Carries the previous month's closing of GHS ${fmtMoney(opening.amount)} into ${month}.`
        : 'Sets the opening balance for this month.',
      tone: 'primary', confirmText: 'Yes, bring forward',
    });
    if (!ok) return;
    try {
      await API.post('/petty/bring-forward', { month });
      swal.success('Petty B/F set');
      loadSnap();
    } catch (e) { swal.error(swal.pickError(e, 'Bring forward failed')); }
  };

  const filteredVouchers = useMemo(() => {
    const q = voucherQ.trim().toLowerCase();
    if (!q) return vouchers;
    return vouchers.filter(v => {
      const haystack = [v.voucher_no, v.exp_cat, v.exp_ty, v.narration, v.request_holder,
        ...((v.items || []).flatMap(it => [it.exp_cat, it.exp_ty, it.narration, it.request_holder]))]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [vouchers, voucherQ]);

  const filteredReim = useMemo(() => {
    const q = reimQ.trim().toLowerCase();
    if (!q) return reimList;
    return reimList.filter(r => [r.reimbursement_no, r.source_account, r.reason, r.status]
      .filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [reimList, reimQ]);

  // Running journal — search + date range
  const inRange = (d, from, to) => {
    if (!from && !to) return true;
    const ds = String(d || '').slice(0, 10);
    if (!ds) return true;
    if (from && ds < from) return false;
    if (to   && ds > to)   return false;
    return true;
  };
  const filteredJournal = useMemo(() => {
    const q = journalQ.trim().toLowerCase();
    return journal.filter(r => {
      if (!inRange(r.date, journalFrom, journalTo)) return false;
      if (!q) return true;
      const haystack = [r.description, r.voucher_no, r.exp_cat, r.exp_ty, r.request_holder, r.side]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [journal, journalQ, journalFrom, journalTo]);

  const reverseVoucher = async (v) => {
    const reason = await swal.prompt({
      title: `Reverse ${v.voucher_no}?`,
      text: `GHS ${fmtMoney(v.amount)} · ${v.exp_ty}`,
      inputLabel: 'Reason',
      inputType: 'textarea',
      confirmText: 'Reverse',
      validate: (val) => (!val || val.trim().length < 5) ? 'Reason ≥ 5 chars' : null,
    });
    if (!reason) return;
    try {
      await API.delete(`/petty/vouchers/${v.id}`, { data: { reason } });
      swal.success('Voucher reversed');
      loadSnap();
    } catch (e) { swal.error(swal.pickError(e, 'Reverse failed')); }
  };

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto', pb: 4 }}>
      <ModuleHeader
        icon={<PettyIcon />}
        title="Petty Cash Book"
        subtitle="Reimbursements from cash/bank into petty cash and the everyday small disbursements"
      />

      {/* Month + actions */}
      <Card sx={{ mb: 2.5, borderRadius: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center">
            <TextField select size="small" label="Working month"
              value={month} onChange={(e) => setMonth(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><MonthIcon fontSize="small" /></InputAdornment> }}>
              {(months.find(m => m.mon_yer === month) ? months : [...months, { mon_yer: month }])
                .map(m => <MenuItem key={m.mon_yer} value={m.mon_yer}>{m.mon_yer}</MenuItem>)}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1}>
              {opening.source !== 'confirmed' && (
                <Tooltip title={opening.source === 'computed'
                  ? `Will carry GHS ${fmtMoney(opening.amount)} from ${opening.bf_from || 'prior month'}`
                  : 'Stamp the opening balance for this month'}>
                  <Button variant="outlined" color="success"
                    startIcon={<BFIcon />}
                    onClick={bringForward}
                    sx={{ fontWeight: 800, textTransform: 'none' }}>
                    Bring Forward
                  </Button>
                </Tooltip>
              )}
              <Button variant="outlined" startIcon={<RequestIcon />}
                onClick={() => setReimOpen(true)}
                sx={{ fontWeight: 800, textTransform: 'none' }}>
                Request Reimbursement
              </Button>
              <Button variant="contained" color="primary" startIcon={<PaymentIcon />}
                onClick={() => setPvOpen(true)}
                disabled={balance <= 0}
                sx={{ fontWeight: 800, textTransform: 'none' }}>
                New Petty Voucher
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* KPIs */}
      <Box sx={{ display: 'grid', gap: 1.5, mb: 2.5,
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' } }}>
        <KpiTile icon={<BFIcon />} label="Opening Balance"
          value={`GHS ${fmtMoney(opening.amount)}`}
          tone={opening.source === 'confirmed' ? 'success' : opening.source === 'computed' ? 'primary' : 'warning'}
          sub={
            opening.source === 'confirmed' ? 'B/F confirmed for this month'
            : opening.source === 'computed' ? `Computed from ${opening.bf_from || 'prior'}`
            : 'No B/F set'
          } />
        <KpiTile icon={<CashIcon />} label="Petty Cash on Hand"
          value={`GHS ${fmtMoney(balance)}`}
          tone={balance > 0 ? 'success' : 'warning'}
          sub={`Closing for ${month}`} />
        <KpiTile icon={<InflowIcon />} label="Reimbursed (this month)"
          value={`GHS ${fmtMoney(summary.reimbursed)}`} tone="primary" />
        <KpiTile icon={<OutflowIcon />} label="Expensed (this month)"
          value={`GHS ${fmtMoney(summary.expensed)}`} tone="error" />
        <KpiTile icon={<RequestIcon />} label="Pending Requests"
          value={`${pendings.length}`} tone="warning"
          sub={pendings.length > 0 ? 'Awaiting approval' : 'All settled'} />
      </Box>

      {loading && <Card sx={{ p: 6, textAlign: 'center', mb: 2 }}><CircularProgress /></Card>}

      {!loading && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
          {/* Reimbursements */}
          <Card sx={{
            borderRadius: 2, overflow: 'hidden',
            border: '1px solid', borderColor: (t) => alpha(t.palette.primary.main, 0.25),
          }}>
            <Box sx={{
              px: 2.5, py: 1.4,
              background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.08)} 0%, ${alpha(t.palette.primary.main, 0.02)} 100%)`,
              borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
            }}>
              <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main' }}>
                <RequestIcon fontSize="small" />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="subtitle2" fontWeight={800}>Reimbursements · {month}</Typography>
                <Typography variant="caption" color="text.secondary">Money pulled from cashbook into petty cash</Typography>
              </Box>
              <Chip size="small" variant="outlined" label={`${filteredReim.length}`} sx={{ fontWeight: 700, bgcolor: 'background.paper' }} />
              <TextField size="small" placeholder="Search…"
                value={reimQ} onChange={(e) => { setReimQ(e.target.value); setReimPage(0); }}
                sx={{ minWidth: 180, bgcolor: 'background.paper', borderRadius: 1 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            </Box>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                {filteredReim.length === 0 && (
                  <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
                    No reimbursements yet for this month.
                  </Typography>
                )}
                {filteredReim.slice(reimPage * reimRpp, (reimPage + 1) * reimRpp).map(r => {
                  const accent =
                    r.status === 'APPROVED' ? '#2e7d32'
                  : r.status === 'PENDING'  ? '#ed6c02'
                  : r.status === 'REJECTED' ? '#d32f2f'
                  :                           '#1565C0';
                  return (
                    <MobileDataCard key={r.id}
                      accent={accent}
                      title={r.reimbursement_no}
                      subtitle={`${fmtDate(r.request_date)} · ${r.source_account}`}
                      status={r.status}
                      statusColor={
                        r.status === 'APPROVED' ? 'success'
                      : r.status === 'PENDING'  ? 'warning'
                      : r.status === 'REJECTED' ? 'error'
                      : r.status === 'LEGACY'   ? 'info'
                      :                           'default'
                      }
                      fields={[{ label: 'Reason', value: r.reason }]}
                      amount={`GHS ${fmtMoney(r.amount)}`}
                      amountColor="primary.main"
                    />
                  );
                })}
              </Box>
            ) : (
            <TableContainer sx={{ maxHeight: 460 }}>
              <Table stickyHeader size="small" sx={{
                '& .MuiTableCell-head': {
                  bgcolor: '#fafbfc', fontWeight: 800, fontSize: 10.5,
                  textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary',
                  borderBottom: '2px solid', borderColor: 'primary.light', py: 1.1,
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Ref</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReim.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center"
                      sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                      No reimbursements yet for this month.
                    </TableCell></TableRow>
                  )}
                  {filteredReim.slice(reimPage * reimRpp, (reimPage + 1) * reimRpp).map(r => (
                    <TableRow key={r.id} hover sx={{ '& td': { fontSize: 12, py: 0.85, ...TNUM } }}>
                      <TableCell sx={{ fontWeight: 700, fontFamily: FONT_MONO, fontSize: 11.5 }}>{r.reimbursement_no}</TableCell>
                      <TableCell>{fmtDate(r.request_date)}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{r.source_account}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Tooltip title={r.reason}><span>{r.reason}</span></Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: 'primary.main' }}>
                        GHS {fmtMoney(r.amount)}
                      </TableCell>
                      <TableCell>
                        <Chip size="small"
                          color={
                            r.status === 'APPROVED' ? 'success'
                            : r.status === 'PENDING' ? 'warning'
                            : r.status === 'REJECTED' ? 'error'
                            : r.status === 'LEGACY' ? 'info'
                            : 'default'
                          }
                          label={r.status}
                          sx={{ fontWeight: 800, height: 20 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}
            <TablePagination component="div"
              count={filteredReim.length} page={reimPage}
              onPageChange={(_, p) => setReimPage(p)}
              rowsPerPage={reimRpp}
              onRowsPerPageChange={(e) => { setReimRpp(Number(e.target.value)); setReimPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }} />
          </Card>

          {/* Vouchers */}
          <Card sx={{
            borderRadius: 2, overflow: 'hidden',
            border: '1px solid', borderColor: (t) => alpha(t.palette.error.main, 0.25),
          }}>
            <Box sx={{
              px: 2.5, py: 1.4,
              background: (t) => `linear-gradient(135deg, ${alpha(t.palette.error.main, 0.08)} 0%, ${alpha(t.palette.error.main, 0.02)} 100%)`,
              borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
            }}>
              <Avatar sx={{ width: 30, height: 30, bgcolor: 'error.main' }}>
                <VoucherIcon fontSize="small" />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="subtitle2" fontWeight={800}>Petty Vouchers · {month}</Typography>
                <Typography variant="caption" color="text.secondary">Outgoing petty cash spend</Typography>
              </Box>
              <Chip size="small" variant="outlined" label={`${filteredVouchers.length}`} sx={{ fontWeight: 700, bgcolor: 'background.paper' }} />
              <TextField size="small" placeholder="Search…"
                value={voucherQ} onChange={(e) => { setVoucherQ(e.target.value); setVoucherPage(0); }}
                sx={{ minWidth: 180, bgcolor: 'background.paper', borderRadius: 1 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            </Box>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                {filteredVouchers.length === 0 && (
                  <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
                    No petty vouchers yet for this month.
                  </Typography>
                )}
                {filteredVouchers.slice(voucherPage * voucherRpp, (voucherPage + 1) * voucherRpp).map(v => {
                  const isReversed = v.status === 'REVERSED';
                  const itemsCount = (v.items || []).length;
                  return (
                    <MobileDataCard key={v.id}
                      accent={isReversed ? '#d32f2f' : '#2e7d32'}
                      title={v.voucher_no}
                      subtitle={`${fmtDate(v.pay_date)} · ${v.exp_cat}`}
                      status={v.status}
                      statusColor={isReversed ? 'error' : 'success'}
                      fields={[
                        { label: 'Type',
                          value: itemsCount > 1
                            ? `${v.exp_ty} (+${itemsCount - 1} more)`
                            : v.exp_ty,
                          bold: true },
                      ]}
                      amount={`GHS ${fmtMoney(v.amount)}`}
                      amountColor="error.main"
                      actions={v.status === 'POSTED' ? (
                        <Button size="small" variant="outlined" color="error"
                          startIcon={<DeleteIcon fontSize="small" />}
                          onClick={() => reverseVoucher(v)}
                          sx={{ textTransform: 'none', fontWeight: 700 }}>
                          Reverse
                        </Button>
                      ) : null}
                    />
                  );
                })}
              </Box>
            ) : (
            <TableContainer sx={{ maxHeight: 460 }}>
              <Table stickyHeader size="small" sx={{
                '& .MuiTableCell-head': {
                  bgcolor: '#fafbfc', fontWeight: 800, fontSize: 10.5,
                  textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary',
                  borderBottom: '2px solid', borderColor: 'error.light', py: 1.1,
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell>PCV No.</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" sx={{ width: 60 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVouchers.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center"
                      sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                      No petty vouchers yet for this month.
                    </TableCell></TableRow>
                  )}
                  {filteredVouchers.slice(voucherPage * voucherRpp, (voucherPage + 1) * voucherRpp).map(v => (
                    <TableRow key={v.id} hover sx={{ '& td': { fontSize: 12, py: 0.85, ...TNUM },
                      opacity: v.status === 'REVERSED' ? 0.55 : 1 }}>
                      <TableCell sx={{ fontWeight: 700, fontFamily: FONT_MONO, fontSize: 11.5 }}>{v.voucher_no}</TableCell>
                      <TableCell>{fmtDate(v.pay_date)}</TableCell>
                      <TableCell>
                        <Tooltip title={(v.items || []).map(it => `${it.exp_ty} · GHS ${fmtMoney(it.amount)}${it.narration ? ' · ' + it.narration : ''}`).join('\n')}>
                          <Box>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="body2" fontWeight={700} fontSize={12}>{v.exp_ty}</Typography>
                              {v.items && v.items.length > 1 && (
                                <Chip size="small" color="warning" variant="outlined" label={`+${v.items.length - 1}`}
                                  sx={{ height: 16, fontSize: 9.5, fontWeight: 800, '& .MuiChip-label': { px: 0.5 } }} />
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">{v.exp_cat}</Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: 'error.main' }}>
                        GHS {fmtMoney(v.amount)}
                      </TableCell>
                      <TableCell>
                        <Chip size="small"
                          color={v.status === 'REVERSED' ? 'error' : 'success'}
                          label={v.status} sx={{ fontWeight: 800, height: 20 }} />
                      </TableCell>
                      <TableCell align="right">
                        {v.status === 'POSTED' && (
                          <Tooltip title="Reverse voucher">
                            <IconButton size="small" color="error" onClick={() => reverseVoucher(v)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}
            <TablePagination component="div"
              count={filteredVouchers.length} page={voucherPage}
              onPageChange={(_, p) => setVoucherPage(p)}
              rowsPerPage={voucherRpp}
              onRowsPerPageChange={(e) => { setVoucherRpp(Number(e.target.value)); setVoucherPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }} />
          </Card>
        </Box>
      )}

      {/* Running journal — same style as the Cashbook running journal */}
      {!loading && (
        <Card sx={{
          borderRadius: 2, overflow: 'hidden',
          border: '1px solid',
          borderColor: (t) => alpha(t.palette.primary.main, 0.25),
          boxShadow: (t) => `0 2px 8px ${alpha(t.palette.primary.main, 0.06)}`,
        }}>
          {/* Title + search row */}
          <Box sx={{
            px: 2.5, py: 1.4,
            background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.08)} 0%, ${alpha(t.palette.primary.main, 0.02)} 100%)`,
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main', fontSize: 14 }}>
              <PettyIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                Petty Cash Running Journal · {month}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Every reimbursement and voucher in date order
              </Typography>
            </Box>
            <Chip size="small" variant="outlined"
              label={`${filteredJournal.length} row${filteredJournal.length === 1 ? '' : 's'}`}
              sx={{ fontWeight: 700, bgcolor: 'background.paper' }} />
            <TextField size="small" placeholder="Search description, voucher, type…"
              value={journalQ} onChange={(e) => { setJournalQ(e.target.value); setJournalPage(0); }}
              sx={{ minWidth: 240, bgcolor: 'background.paper', borderRadius: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: journalQ && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setJournalQ('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
          </Box>

          {/* Date range row */}
          <Box sx={{
            px: 2.5, py: 1.1, bgcolor: 'background.paper',
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
          }}>
            <Typography variant="caption" fontWeight={800}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10, color: 'text.secondary' }}>
              Date range
            </Typography>
            <TextField size="small" type="date" label="From"
              InputLabelProps={{ shrink: true }}
              value={journalFrom}
              onChange={(e) => { setJournalFrom(e.target.value); setJournalPage(0); }}
              sx={{ width: 170 }} />
            <TextField size="small" type="date" label="To"
              InputLabelProps={{ shrink: true }}
              value={journalTo}
              onChange={(e) => { setJournalTo(e.target.value); setJournalPage(0); }}
              sx={{ width: 170 }} />
            {(journalFrom || journalTo) && (
              <Button size="small" variant="text" color="inherit"
                onClick={() => { setJournalFrom(''); setJournalTo(''); setJournalPage(0); }}
                startIcon={<CloseIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}>
                Clear dates
              </Button>
            )}
          </Box>

          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
              {filteredJournal.length === 0 && (
                <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
                  {journalQ || journalFrom || journalTo ? 'No rows match the current filter' : 'No movements yet this month.'}
                </Typography>
              )}
              {filteredJournal
                .slice(journalPage * journalRpp, (journalPage + 1) * journalRpp)
                .map(r => {
                  const inAmt  = Number(r.reimbursed || 0);
                  const outAmt = Number(r.expensed || 0);
                  const isIn = inAmt > 0;
                  return (
                    <MobileDataCard key={r.id}
                      accent={r.is_bf ? '#1565C0' : (isIn ? '#2e7d32' : '#d32f2f')}
                      title={
                        <Stack direction="row" alignItems="center" spacing={0.7}>
                          {r.is_bf && (
                            <Chip size="small" color="primary" label="B/F"
                              sx={{ height: 16, fontSize: 9, fontWeight: 800,
                                '& .MuiChip-label': { px: 0.6 } }} />
                          )}
                          <span>{r.description}</span>
                        </Stack>
                      }
                      subtitle={`${r.date}${r.voucher_no ? ' · ' + r.voucher_no : ''}`}
                      amount={isIn ? `+ ${fmtMoney(inAmt)}` : `- ${fmtMoney(outAmt)}`}
                      amountColor={isIn ? 'success.dark' : 'error.main'}
                      amountLabel={isIn ? 'Reimbursed (In)' : 'Expensed (Out)'}
                      actions={r.transaction_id ? (
                        <Button size="small" variant="text" color="info"
                          startIcon={<DoubleEntryIcon fontSize="small" />}
                          onClick={() => openDoubleEntry(
                            r.transaction_id,
                            `${r.voucher_no || ''} · ${r.date} · ${r.description}`
                          )}
                          sx={{ textTransform: 'none', fontWeight: 700 }}>
                          View DR/CR
                        </Button>
                      ) : null}
                    />
                  );
                })}
            </Box>
          ) : (
          <TableContainer sx={{ maxHeight: 520, bgcolor: 'background.paper' }}>
            <Table stickyHeader size="small" sx={{
              '& .MuiTableCell-head': {
                bgcolor: '#fafbfc', fontWeight: 800, fontSize: 10.5,
                textTransform: 'uppercase', letterSpacing: 0.6,
                color: 'text.secondary',
                borderBottom: '2px solid', borderColor: 'primary.light',
                py: 1.1,
              },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Voucher</TableCell>
                  <TableCell align="right">In</TableCell>
                  <TableCell align="right">Out</TableCell>
                  <TableCell align="center" sx={{ width: 50 }}>DR/CR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredJournal.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center"
                    sx={{ py: 5, color: 'text.secondary', fontStyle: 'italic' }}>
                    {journalQ || journalFrom || journalTo ? 'No rows match the current filter' : 'No movements yet this month.'}
                  </TableCell></TableRow>
                )}
                {filteredJournal
                  .slice(journalPage * journalRpp, (journalPage + 1) * journalRpp)
                  .map(r => {
                    const inAmt  = Number(r.reimbursed || 0);
                    const outAmt = Number(r.expensed || 0);
                    return (
                      <TableRow key={r.id} hover sx={{
                        '& td': { fontSize: 11.5, py: 0.85, ...TNUM,
                          borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.04)' },
                        bgcolor: r.is_bf
                          ? (t) => alpha(t.palette.primary.main, 0.06)
                          : '#ffffff',
                        '&:nth-of-type(even)': r.is_bf ? {} : { bgcolor: '#fcfdfe' },
                        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.04) + ' !important' },
                      }}>
                        <TableCell sx={{ color: 'text.secondary' }}>{r.date}</TableCell>
                        <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Tooltip title={r.description}>
                            <Stack direction="row" alignItems="center" spacing={0.7}>
                              {r.is_bf && (
                                <Chip size="small" color="primary" variant="filled"
                                  label="B/F" sx={{ height: 17, fontSize: 9, fontWeight: 800,
                                    '& .MuiChip-label': { px: 0.7 } }} />
                              )}
                              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {r.description}
                              </Box>
                            </Stack>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontFamily: FONT_MONO, fontSize: 11 }}>{r.voucher_no || '—'}</TableCell>
                        <TableCell align="right"
                          sx={{ color: inAmt > 0 ? 'success.dark' : 'text.disabled', fontWeight: inAmt > 0 ? 700 : 400 }}>
                          {inAmt > 0 ? fmtMoney(inAmt) : '—'}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ color: outAmt > 0 ? 'error.main' : 'text.disabled', fontWeight: outAmt > 0 ? 700 : 400 }}>
                          {outAmt > 0 ? fmtMoney(outAmt) : '—'}
                        </TableCell>
                        <TableCell align="center">
                          {r.transaction_id && (
                            <Tooltip title="View double-entry (DR/CR)">
                              <IconButton size="small" color="info"
                                onClick={() => openDoubleEntry(
                                  r.transaction_id,
                                  `${r.voucher_no || ''} · ${r.date} · ${r.description}`
                                )}>
                                <DoubleEntryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          <TablePagination component="div"
            count={filteredJournal.length}
            page={journalPage}
            onPageChange={(_, p) => setJournalPage(p)}
            rowsPerPage={journalRpp}
            onRowsPerPageChange={(e) => { setJournalRpp(Number(e.target.value)); setJournalPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
          />
        </Card>
      )}

      <ReimbursementDialog open={reimOpen} onClose={() => setReimOpen(false)}
        accounts={accounts} onSaved={() => loadSnap()} />
      <PettyVoucherDialog open={pvOpen} onClose={() => setPvOpen(false)}
        categories={categories} expTypes={expTypes} balance={balance}
        onSaved={() => loadSnap()} />
      <PettyDoubleEntryDialog open={deOpen} onClose={() => setDeOpen(false)}
        uuid={deUuid} label={deLabel} />
    </Box>
  );
}

// ── Double-entry dialog (same shape as the Cashbook one) ──────
function PettyDoubleEntryDialog({ open, onClose, uuid, label }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !uuid) return;
    setLoading(true); setError(''); setData(null);
    API.get(`/cashbook/journal-entry/${uuid}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.message || 'Could not load journal entries'))
      .finally(() => setLoading(false));
  }, [open, uuid]);

  const drLines = (data?.lines || []).filter(l => l.side === 'DR');
  const crLines = (data?.lines || []).filter(l => l.side === 'CR');
  const totalDr = drLines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const totalCr = crLines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.01;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.info.main} 0%, ${t.palette.primary.main} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}><DoubleEntryIcon /></Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800}>Double-Entry View</Typography>
          <Typography variant="caption" sx={{ opacity: 0.92, fontFamily: FONT_MONO }} noWrap>
            {label || uuid}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>
      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !data?.transaction && !error && (
          <Alert severity="info" variant="outlined">
            No journal entry was recorded for this transaction. Older rows may not yet have a journal mirror.
          </Alert>
        )}
        {!loading && data?.transaction && (
          <>
            <Box sx={{
              display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
              gap: 1, mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1.5,
              border: 1, borderColor: 'divider',
            }}>
              <StatBox label="Kind"      value={String(data.transaction.kind || '').toUpperCase()} mono />
              <StatBox label="Date"      value={fmtDate(data.transaction.txn_date)} />
              <StatBox label="Reference" value={data.transaction.reference_no || '—'} mono />
              <StatBox label="Total"     value={`GHS ${fmtMoney(data.transaction.total_amount)}`} highlight />
            </Box>
            <Box sx={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
              border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
              overflow: 'hidden', bgcolor: 'background.paper',
            }}>
              <SideBlock tone="primary" label="DEBIT (DR)" lines={drLines} total={totalDr} />
              <SideBlock tone="success" label="CREDIT (CR)" lines={crLines} total={totalCr} />
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}
              sx={{
                mt: 1.5, py: 0.85, px: 1.4, borderRadius: 1,
                bgcolor: balanced
                  ? (t) => alpha(t.palette.success.main, 0.08)
                  : (t) => alpha(t.palette.error.main, 0.08),
                border: '1px solid',
                borderColor: balanced ? 'success.light' : 'error.light',
              }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                bgcolor: balanced ? 'success.main' : 'error.main' }} />
              <Typography variant="caption" fontWeight={800}
                sx={{ color: balanced ? 'success.dark' : 'error.dark' }}>
                {balanced
                  ? `Balanced — Σ DR = Σ CR = GHS ${fmtMoney(totalDr)}`
                  : `Imbalance: GHS ${fmtMoney(Math.abs(totalDr - totalCr))}`}
              </Typography>
            </Stack>
            {data.transaction.memo && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'background.paper',
                border: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10 }}>
                  Memo
                </Typography>
                <Typography variant="body2">{data.transaction.memo}</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.7 }}>
        <Button onClick={onClose} variant="contained" color="primary"
          sx={{ fontWeight: 800, textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function StatBox({ label, value, mono, highlight }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{
        fontFamily: mono ? FONT_MONO : undefined,
        fontWeight: 800, fontSize: 14,
        color: highlight ? 'error.main' : 'text.primary',
        ...TNUM,
      }} noWrap>{value}</Typography>
    </Box>
  );
}

function SideBlock({ tone, label, lines, total }) {
  return (
    <Box sx={{
      ...(tone === 'primary' ? { borderRight: '1px solid', borderColor: 'divider' } : {}),
      bgcolor: (t) => alpha(t.palette[tone].main, 0.04),
    }}>
      <Box sx={{
        px: 2, py: 0.8,
        bgcolor: (t) => alpha(t.palette[tone].main, 0.10),
        borderBottom: '1px solid', borderColor: 'divider',
        textAlign: 'center',
      }}>
        <Typography variant="caption" fontWeight={800}
          sx={{ letterSpacing: 1.2, fontSize: 11, color: `${tone}.main` }}>
          {label}
        </Typography>
      </Box>
      {lines.length === 0 ? (
        <Box sx={{ px: 2, py: 2, color: 'text.disabled', fontStyle: 'italic', textAlign: 'center' }}>
          <Typography variant="body2">No lines</Typography>
        </Box>
      ) : lines.map((l, i) => (
        <Box key={i} sx={{
          px: 2, py: 1.1,
          borderBottom: i === lines.length - 1 ? 'none' : '1px dashed',
          borderColor: (t) => alpha(t.palette[tone].main, 0.18),
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={800} fontSize={13} noWrap>{l.name}</Typography>
            <Typography variant="caption" color="text.secondary"
              sx={{ fontFamily: FONT_MONO, fontSize: 10 }}>
              {l.code} · {l.type}
            </Typography>
          </Box>
          <Typography sx={{ ...TNUM, fontWeight: 800, fontSize: 14, color: `${tone}.dark` }}>
            GHS {fmtMoney(l.amount)}
          </Typography>
        </Box>
      ))}
      <Box sx={{
        px: 2, py: 1, borderTop: '2px solid',
        borderColor: (t) => alpha(t.palette[tone].main, 0.4),
        bgcolor: (t) => alpha(t.palette[tone].main, 0.10),
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Typography variant="caption" fontWeight={900}
          sx={{ letterSpacing: 1, fontSize: 11, color: `${tone}.main` }}>
          Σ {tone === 'primary' ? 'DR' : 'CR'}
        </Typography>
        <Typography sx={{ ...TNUM, fontWeight: 900, fontSize: 14.5, color: `${tone}.dark` }}>
          GHS {fmtMoney(total)}
        </Typography>
      </Box>
    </Box>
  );
}
